import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import qs from 'qs';

import { Grid, Typography } from '@material-ui/core';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import OTP from '../../../components/OTP';

import { LockOutlined, ArrowBack } from '@material-ui/icons';

import { loginStart, loginPassword, loginVerifyCode } from '../store/thunk';
import { handleLogout, setLoginValues, setLoginStep } from '../store/actions';
import { clearProfile } from '../../Profile/store/actions';
import { clearBusinessInfo } from '../../BusinessInfo/store/actions';

import { useStyles } from './styles';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { emailRegExp } from 'helpers/regex';
import { renderTurnstileWhenReady, isTurnstileWidgetVisible } from 'helpers/turnstile';

import projectPackage from '../../../../package.json';

const TURNSTILE_VISIBILITY_CHECK_INTERVAL_MS = 500;
const TURNSTILE_VISIBILITY_TIMEOUT_MS = 15000;

const logoJumpPark = require('../../../assets/img/logo_login.svg');
const logoJumpCar = require('../../../assets/img/logo_login_jump_car.svg');
const backgroundJumpPark = require('../../../assets/img/login_background.png');
const backgroundJumpCar = require('../../../assets/img/login_background_jump_car.png');

const Login = () => {
  const dispatch = useDispatch();

  const classes = useStyles();

  const { width, height } = useWindowDimensions();
  const isMobile = width < 960;

  const history = useHistory();
  const location = useLocation();

  const resaleId = process.env.REACT_APP_RESALE_ID;

  const {
    loginValues,
    loginSteps,
    loginAttemptToken,
    challengeToken,
    loggedIn,
    establishments
  } = useSelector(state => state.login);

  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileRenderKey, setTurnstileRenderKey] = useState(0);
  const [turnstileErrorCode, setTurnstileErrorCode] = useState('');
  const [isTurnstileMissing, setIsTurnstileMissing] = useState(false);

  const hasTurnstileFailed = !!turnstileErrorCode || isTurnstileMissing;

  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  const [loginValuesErrors, setLoginValuesErrors] = useState({
    email: '',
    password: '',
    code: '',
    turnstile: ''
  });

  const isInvoiceLoginUrl = location.pathname == '/area-cliente';
  const isCreditCardUrl = location.pathname == '/cadastro-cartao';

  const doesUserHaveAnyEstablishment =
    establishments[0]?.establishmentId || establishments[establishments.length - 1]?.establishmentId;

  const isJumpPark =
    window.location.hostname == 'localhost' ||
    window.location.hostname == 'teste-admin.jumppark.com.br' ||
    window.location.hostname == 'admin.jumppark.com.br';

  useEffect(() => {
    const notAuthorized = qs.parse(location.search, { ignoreQueryPrefix: true }).notauthorized;

    if(notAuthorized) {
      dispatch(handleLogout());
      toast.error('Ação não autorizada. Por favor, efetue o login antes de continuar.');
    }

    if(loggedIn && isInvoiceLoginUrl) {
      return history.push('/suas-assinaturas');
    }

    if(loggedIn && isCreditCardUrl) {
      return history.push({
        pathname: '/suas-assinaturas',
        state: {
          data: {
            isFromLogin: true
          }
        }
      });
    }

    if(loggedIn && doesUserHaveAnyEstablishment) {
      return history.push('/visao-geral');
    }
  }, []);

  useEffect(() => {
    if(loginSteps != 'email') {
      return undefined;
    }

    const container = turnstileContainerRef.current;

    return renderTurnstileWhenReady({
      container,
      widgetIdRef: turnstileWidgetIdRef,
      options: {
        sitekey: process.env.REACT_APP_CLOUDFLARE_TURNSTILE_KEY,
        theme: 'light',
        language: 'pt-BR',
        appearance: 'always',
        callback: token => {
          setTurnstileToken(token);
          setTurnstileErrorCode('');
          setIsTurnstileMissing(false);
          setLoginValuesErrors(current => ({
            ...current,
            turnstile: ''
          }));
        },
        'error-callback': errorCode => setTurnstileErrorCode(String(errorCode))
      }
    });
  }, [loginSteps, turnstileRenderKey]);

  useEffect(() => {
    if(loginSteps != 'email') {
      return undefined;
    }

    const deadline = Date.now() + TURNSTILE_VISIBILITY_TIMEOUT_MS;

    const intervalId = setInterval(() => {
      if(isTurnstileWidgetVisible(turnstileContainerRef.current)) {
        clearInterval(intervalId);
        return;
      }

      if(Date.now() < deadline) {
        return;
      }

      clearInterval(intervalId);
      setIsTurnstileMissing(true);
    }, TURNSTILE_VISIBILITY_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loginSteps, turnstileRenderKey]);

  useEffect(() => {
    if(loginSteps == 'email') {
      dispatch(setLoginValues('password', ''));
    }

    if(loginSteps == 'password') {
      dispatch(setLoginValues('code', ''));
    }
  }, [loginSteps]);

  const handleLogin = values => {
    dispatch(clearProfile());

    setLoginValuesErrors({
      email: '',
      password: '',
      code: '',
      turnstile: ''
    });

    if(loginSteps == 'email_code') {
      if(!values?.code) {
        return setLoginValuesErrors({
          ...loginValuesErrors,
          code: 'O código de autenticação é obrigatório'
        });
      }

      if(values?.code?.length < 6) {
        return setLoginValuesErrors({
          ...loginValuesErrors,
          code: 'O código de autenticação deve ter 6 dígitos'
        });
      }

      setLoading(true);

      return dispatch(loginVerifyCode(challengeToken, values?.code, history))
        .finally(() => setLoading(false));
    }

    if(loginSteps == 'password') {
      if(!values?.password) {
        return setLoginValuesErrors({
          ...loginValuesErrors,
          password: 'A senha é obrigatória'
        });
      }

      setLoading(true);

      return dispatch(loginPassword(loginAttemptToken, values?.password, history))
        .finally(() => setLoading(false));
    }

    if(loginSteps == 'email') {
      if(!turnstileToken) {
        return setLoginValuesErrors({
          ...loginValuesErrors,
          turnstile: 'Por favor, verifique o CAPTCHA'
        });
      }

      if(!values?.email) {
        return setLoginValuesErrors({
          ...loginValuesErrors,
          email: 'O e-mail é obrigatório'
        });
      }

      if(!emailRegExp.test(values?.email)) {
        return setLoginValuesErrors({
          ...loginValuesErrors,
          email: 'O e-mail precisa ser válido'
        });
      }

      setLoading(true);

      return dispatch(loginStart(values?.email, turnstileToken))
        .finally(() => setLoading(false));
    }
  }

  const handleRegistration = () => {
    dispatch(clearProfile());
    dispatch(clearBusinessInfo());

    localStorage.clear();
  }

  const handleTurnstileReload = () => {
    localStorage.clear();

    setTurnstileToken('');
    setTurnstileErrorCode('');
    setIsTurnstileMissing(false);
    setTurnstileRenderKey(key => key + 1);
  }

  const handleTurnstileErrorClose = () => {
    setTurnstileErrorCode('');
    setIsTurnstileMissing(false);
  }

  const getSloganTop = () => {
    if(height < 600) {
      return '15%';
    }

    if(height < 700) {
      return '30%';
    }

    if(height < 850) {
      return '40%';
    }

    return '50%';
  }

  const getTurnstileZoom = () => {
    if(width >= 960 && width < 1100) {
      return 0.75;
    }

    if(width >= 1100 && width < 1300) {
      return 0.9;
    }

    return 1;
  }

  return(
    <Grid container className={classes.wrapper}>
      {hasTurnstileFailed && (
        <Modal
          id="turnstileErrorModal"
          title="Erro no recaptcha"
          scroll="body"
          maxWidth="sm"
          open={hasTurnstileFailed}
          onClose={handleTurnstileErrorClose}
          modalActions={
            <Button
              type="button"
              color="primary"
              onClick={handleTurnstileReload}
            >
              Recarregar
            </Button>
          }
        >
          {!!turnstileErrorCode ? (
            <Typography>
              Problema ao carregar o Recaptcha da Cloudflare. Não foi possível carregar devido ao erro de código {turnstileErrorCode}.
            </Typography>
          ) : (
            <Typography>
              Problema ao carregar o Recaptcha da Cloudflare.
            </Typography>
          )}
        </Modal>
      )}
      {!isMobile && (
        <div
          style={{ top: getSloganTop() }}
          className={classes.slogan}
        >
          <Typography variant="h5">
            {isJumpPark ? (
              <b>Controle o hoje, conquiste o <br /> amanhã_</b>
            ) : (
              <b>Alcance resultados, supere <br /> expectativas_</b>
            )}
          </Typography>
          <div className={classes.sloganVerticalAdornment} />
          <div className={classes.sloganHorizontalAdornment} />
        </div>
      )}
      <Grid
        item
        md={6}
        xs={12}
        style={{ backgroundImage: `url(${isJumpPark ? backgroundJumpPark : backgroundJumpCar})` }}
        className={[classes.containerBackground, isMobile ? classes.halfVh : classes.fullVh]}
      >
        <Grid className={isJumpPark ? classes.containerBackgroundJumpParkGradient : classes.containerBackgroundJumpCarGradient} />
      </Grid>
      {!isMobile && (
        <Grid
          item
          md={3}
          xs={12}
          className={[classes.containerSecondary, isMobile ? classes.halfVh : classes.fullVh]}
        >
          <Typography
            color="#212121"
            variant="h5"
            style={{ whiteSpace: 'nowrap' }}
            className={classes.containerFloatingComponent}
          >
            Juntos nós podemos
          </Typography>
        </Grid>
      )}
      <Grid
        item
        md={3}
        xs={12}
        className={[classes.containerLogin, !isMobile && classes.fullVh]}
      >
        <div className={isMobile && classes.containerFormAndSloganMobile}>
          <div
            className={isMobile ? classes.containerFormMobile : classes.containerForm}
            style={{ padding: 30 }}
          >
            <img
              alt="Jump Park"
              src={isJumpPark ? logoJumpPark : logoJumpCar}
              className={classes.logo}
            />
            <form
              onSubmit={event => {
                event.preventDefault();
                return handleLogin(loginValues);
              }}
              onKeyDown={event => {
                if(event.key == 'Enter') {
                  event.preventDefault();
                  return handleLogin(loginValues);
                }
              }}
            >
              {(loginSteps == 'email') && (
                <Input
                  name="email"
                  type="email"
                  label="Email"
                  value={loginValues?.email}
                  error={!!loginValuesErrors.email}
                  helperText={loginValuesErrors.email}
                  onChange={event => dispatch(setLoginValues(event.target.name, event.target.value?.trim()))}
                />
              )}
              {(loginSteps == 'email') && (
                <div style={{ width: '100%' }}>
                  <div
                    key={turnstileRenderKey}
                    ref={turnstileContainerRef}
                    style={{ zoom: getTurnstileZoom() }}
                  />
                  {!!loginValuesErrors.turnstile && (
                    <Typography variant='body2' color='error'>
                      {loginValuesErrors.turnstile}
                    </Typography>
                  )}
                </div>
              )}
              {(loginSteps == 'password') && (
                <Input
                  name="password"
                  type="password"
                  label="Senha"
                  value={loginValues.password}
                  error={!!loginValuesErrors.password}
                  helperText={loginValuesErrors.password}
                  onChange={event => dispatch(setLoginValues(event.target.name, event.target.value))}
                />
              )}
              {(loginSteps == 'email_code') && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 50, height: 50, backgroundColor: 'rgb(2, 42, 92, 0.2)', borderRadius: '50%' }}>
                      <LockOutlined
                        style={{ color: '#022A5C' }}
                        fontSize="large"
                      />
                    </div>
                  </div>
                  <Typography variant="h5">
                    Preencha o código
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Enviamos um código por email
                  </Typography>
                  <OTP
                    value={loginValues.code}
                    error={!!loginValuesErrors.code}
                    helperText={loginValuesErrors.code}
                    onChange={code => dispatch(setLoginValues('code', code))}
                  />
                </>
              )}
              <Button
                fullWidth
                verticalGutter
                type="submit"
                color="#022A5C"
                loading={loading}
              >
                Entrar
              </Button>
              {(loginSteps == 'password' || loginSteps == 'email_code') && (
                <Typography
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}
                  variant="body1"
                  color="textSecondary"
                  component={Link}
                  onClick={() => {
                    if(loginSteps == 'password') {
                      dispatch(setLoginStep('email'));
                    }

                    if(loginSteps == 'email_code') {
                      dispatch(setLoginStep('password'));
                    }
                  }}
                >
                  <ArrowBack fontSize='small' />
                  Voltar
                </Typography>
              )}
            </form>
            <Grid>
              {(loginSteps == 'email' && !isInvoiceLoginUrl) && (
                <Typography
                  to="/esqueci-a-senha"
                  variant="body1"
                  color="textSecondary"
                  component={Link}
                  onClick={() => handleRegistration()}
                >
                  Esqueci minha senha
                </Typography>
              )}
              <Typography color='textSecondary' style={{ marginTop: 10 }}>Versão {projectPackage.version}</Typography>
              {isMobile && (
                <Typography color="textSecondary" style={{ margin: '30px 0 30px 0' }}>
                  <b>Juntos nós podemos</b>
                </Typography>
              )}
              {isMobile && (
                <>
                  {!isInvoiceLoginUrl && (
                    <Link
                      to={{
                        pathname: resaleId ? `/${resaleId}/cadastro-inicial` : '/cadastro-inicial',
                        state: {
                          isRegister: true
                        }
                      }}
                      onClick={() => handleRegistration()}
                    >
                      <Button
                        fullWidth
                        color="#FF7D34"
                        style={{ whiteSpace: 'nowrap', padding: 15 }}
                      >
                        Experimente por 7 dias
                      </Button>
                    </Link>
                  )}
                </>
              )}
            </Grid>
          </div>
          {isMobile && (
            <div className={classes.sloganMobile}>
              <Typography variant="h5">
                {isJumpPark ? (
                  <b>Controle o hoje, conquiste o <br /> amanhã_</b>
                ) : (
                  <b>Alcance resultados, supere <br /> expectativas_</b>
                )}
              </Typography>
            </div>
          )}
        </div>
        {!isMobile && (
          <>
            {(loginSteps == 'email' && !isInvoiceLoginUrl) && (
              <div
                className={classes.containerFloatingComponent}
                style={{ width: '100%', padding: '0 30px 0 30px' }}
              >
                <Link
                  to={{
                    pathname: resaleId ? `/${resaleId}/cadastro-inicial` : '/cadastro-inicial',
                    state: {
                      isRegister: true
                    }
                  }}
                  onClick={() => handleRegistration()}
                >
                  <Button
                    fullWidth
                    color="#FF7D34"
                    style={{ whiteSpace: 'nowrap', padding: 15, marginBottom: -15 }}
                  >
                    Experimente por 7 dias
                  </Button>
                </Link>
              </div>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default Login;